import { Injectable } from '@angular/core';
import { protectedResources } from '../auth-config';
import { HttpClient } from '@angular/common/http';
import { Cluster, Project, ProjectInput } from '../models/project';
import { Observable, shareReplay, retry, map } from 'rxjs';
import { normalizeProjectRole } from '../helpers/project-role';

@Injectable({
  providedIn: 'root',
})

export class ProjectService {
  url = protectedResources.apiProjects.endpoint;
  readerUrl = protectedResources.apiReaderProjects.endpoint;
  clusterUrl = protectedResources.apiClusters.endpoint;
  private retryCount = 3;
  private retryDelay = 1000; // 1 second delay between retries

  constructor(private httpClient: HttpClient) {}

  private normalizeProject(project: Project): Project {
    return {
      ...project,
      projectRole: normalizeProjectRole(project?.projectRole),
    };
  }

  private normalizeProjects(projects: Project[]): Project[] {
    return (projects ?? []).map((project) => this.normalizeProject(project));
  }

  public getProjects(): Observable<Project[]> {
    return this.httpClient
      .get<Project[]>(`${this.readerUrl}`)
      .pipe(
        map((projects) => this.normalizeProjects(projects)),
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getOwnedProjects(): Observable<Project[]> {
    return this.httpClient
      .get<Project[]>(`${this.readerUrl}/owned`)
      .pipe(
        map((projects) => this.normalizeProjects(projects)),
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getProjectChildren(projectId: string): Observable<Project[]> {
    return this.httpClient
      .get<Project[]>(`${this.readerUrl}/${projectId}/children`)
      .pipe(
        map((projects) => this.normalizeProjects(projects)),
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public searchProjects(searchTerm: string): Observable<Project[]> {
    return this.httpClient
      .get<Project[]>(`${this.url}/Search`, {
        params: { subscriptionId: searchTerm }
      })
      .pipe(
        map((projects) => this.normalizeProjects(projects)),
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getProject(projectId: string, forceRefresh: boolean = false): Observable<Project> {
    const requestOptions = forceRefresh
      ? {
          params: { _ts: Date.now().toString() },
        }
      : undefined;

    return this.httpClient
      .get<Project>(`${this.url}/${projectId}`, requestOptions)
      .pipe(
        map((project) => this.normalizeProject(project)),
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getProjectSubscriptions(
    projectId: string,
    pageNumber: number = 0,
    pageSize: number = 10,
    searchTerm?: string
  ): Observable<any> {
    let params: any = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    };
    
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    return this.httpClient
      .get<any>(`${this.url}/${projectId}/subscriptions`, { params })
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getProjectClusters(
    projectId: string,
    pageNumber: number = 0,
    pageSize: number = 10,
    searchTerm?: string
  ): Observable<any> {
    let params: any = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    };
    
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    return this.httpClient
      .get<any>(`${this.url}/${projectId}/clusters`, { params })
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public createProject(projectInput: ProjectInput): Observable<Project> {
    // return throwError(
    //   () =>
    //     new HttpErrorResponse({
    //       status: 503,
    //       statusText: 'Service Unavailable',
    //       error: { detail: 'Mocked 503 error for testing' },
    //     })
    // );

    return this.httpClient
      .post<Project>(`${this.url}`, projectInput)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public updateProject(projectInput: ProjectInput): Observable<Project> {
    return this.httpClient
      .put<Project>(`${this.url}?projectId=${projectInput.id}`, projectInput)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public deleteProject(projectId: string): Observable<Project> {
    return this.httpClient
      .delete<Project>(`${this.url}/Delete?projectId=${projectId}`)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public updateAccessReview(projectId: string): Observable<Project> {
    return this.httpClient
      .get<Project>(`${this.url}/AccessReview?projectId=${projectId}`)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getClusters(): Observable<Cluster> {
    return this.httpClient
      .get<Cluster>(`${this.clusterUrl}`)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  public getProjectsByName(name: string) {
    return this.httpClient
      .get<any>(`${this.url}/SearchByName?name=${name}`)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        shareReplay(1)
      );
  }

  // getProjects() {
  //   return this.http.get<Project[]>(this.url);
  // }

  // getProject(projectId: string) {
  //   return this.http.get<Project>(`${this.url}/${projectId}`);
  // }

  // createProject(projectInput: ProjectInput) {
  //   return this.http.post<Project>(this.url, projectInput);
  // }

  // updateProject(projectInput: ProjectInput) {
  //   return this.http.put<Project>(
  //     `${this.url}?projectId=${projectInput.id}`,
  //     projectInput
  //   );
  // }

  // updateAccessReview(projectId: string) {
  //   return this.http.get<Project>(
  //     `${this.url}/AccessReview?projectId=${projectId}`
  //   );
  // }
}
